import React, { useMemo } from 'react';
import { Animation } from '@crabs-ai/web-icons';
import {
  TIMELINE_HEADER_HEIGHT,
  TIMELINE_HORIZONTAL_PADDING,
} from '../constants';
import { useAllItems, useTracks } from '../utils/use-context';
import { useAnimationDropPreview } from './animation-drop-preview-provider';
import { remainOriginalTrackHeights } from './utils/drag/remain-original-track-heights';

import { ANIMATION_META } from '../items/animation/animation-item-type';

interface AnimationDropPreviewProps {
  timelineWidth: number;
}

// 프리뷰 박스 크기 (인디케이터와 동일하게 28px로 조정)
const PREVIEW_HEIGHT = 28;
const ICON_SIZE = 14;

export const AnimationDropPreview: React.FC<AnimationDropPreviewProps> = ({
  timelineWidth,
}) => {
  const { previewState } = useAnimationDropPreview();
  const { tracks } = useTracks();
  const { items } = useAllItems();

  // 트랙 레이아웃 계산
  const tracksAndLayout = useMemo(() => {
    return remainOriginalTrackHeights({
      originalTracks: tracks,
      originalItems: items,
      newTracks: tracks,
      newItems: items,
    });
  }, [tracks, items]);

  if (!previewState) {
    return null;
  }

  const { isAvailable, trackIndex, itemLeft, itemWidth, animationType, name } =
    previewState;

  // 트랙 위치 계산
  const trackLayout = tracksAndLayout[trackIndex];
  if (!trackLayout) {
    return null;
  }

  // 텍스트 라벨
  const label = animationType ? ANIMATION_META[animationType].name : name;

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-visible"
      style={{ zIndex: 101 }}
    >
      {/* 아이템 하이라이트 */}
      <div
        className={
          isAvailable ? 'bg-sys-style-primary-default/20' : 'bg-red-500/20'
        }
        style={{
          position: 'absolute',
          left: itemLeft + TIMELINE_HORIZONTAL_PADDING,
          top: TIMELINE_HEADER_HEIGHT + trackLayout.top,
          width: itemWidth,
          height: trackLayout.height,
          borderRadius: 8,
          border: isAvailable
            ? '3px solid var(--sys-style-primary-default)'
            : '3px solid rgba(239, 68, 68, 0.8)',
        }}
      />

      {/* 애니메이션 프리뷰 박스 - Figma 디자인 반영 (TimelineItemAnimationIndicator와 동일 스타일) */}
      <div
        className={
          isAvailable
            ? 'bg-sys-bg-dim-thicker text-white backdrop-blur-sm'
            : 'bg-red-500 text-white'
        }
        style={{
          position: 'absolute',
          left: itemLeft + TIMELINE_HORIZONTAL_PADDING + 8,
          // 트랙 하단에서 8px 위로 배치
          top:
            TIMELINE_HEADER_HEIGHT +
            trackLayout.top +
            trackLayout.height -
            PREVIEW_HEIGHT -
            8,

          height: PREVIEW_HEIGHT,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: 8,
          paddingRight: 8,
          gap: 6,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          maxWidth: Math.max(itemWidth - 16, 48),
          overflow: 'hidden',
        }}
      >
        {isAvailable ? (
          <>
            <Animation
              width={ICON_SIZE}
              height={ICON_SIZE}
              className="[&_path]:fill-white"
            />
            <span className="text-label-medium font-medium whitespace-nowrap">
              {label}
            </span>
          </>
        ) : (
          <div className="flex size-full items-center justify-center">
            <svg
              width={16}
              height={16}
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M6 6L18 18M6 18L18 6"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};
